'use client'

import { Partner } from '@/lib/api/common/interfaces/partner.interface'
import { searchAccessiblePartners, searchPartners } from '@/lib/api/partners/partners-api'
import { useAuth } from '@/store/hooks'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAnchoredDropdown } from '@/components/use-anchored-dropdown'
import { MdBusiness, MdClose, MdPerson, MdSearch } from 'react-icons/md'

interface Props {
    value: number | null
    onChange: (partnerId: number | null, partner: Partner | null) => void
    label?: string
    placeholder?: string
    disabled?: boolean
    className?: string
    size?: 'sm' | 'md'
    allowedIds?: number[]
    priceLabelById?: Record<number, string>
    /**
     * Lista pré-carregada de parceiros (ex: vindos de um catálogo).
     * Quando passado, o componente NÃO faz chamada à API — apenas filtra
     * localmente pelo termo de busca.
     */
    items?: Partner[]
    /**
     * Escopo da busca. Sem isto, vale a unidade ativa do usuário (o `CompanySelector` do
     * topo) — o default histórico. Telas que exibem dados de outra filial ou da organização
     * inteira precisam informar o escopo, senão as opções ficam menores que os dados na
     * tela e um registro visível fica impossível de filtrar.
     */
    scope?: 'company' | 'organization'
    /** Empresa da busca quando `scope` é `company`. Default: a unidade ativa. */
    companyId?: number
}

export function PartnerSelector({
    value,
    onChange,
    label = 'Parceiro',
    placeholder = 'Selecione um parceiro (opcional)',
    disabled = false,
    className = '',
    size = 'md',
    allowedIds,
    priceLabelById,
    items,
    scope = 'company',
    companyId: companyIdProp,
}: Props) {
    const hasItems = Array.isArray(items)
    const { user } = useAuth()
    const isOrgScope = scope === 'organization'
    const companyId = companyIdProp ?? user?.companyId
    const [results, setResults] = useState<Partner[]>([])
    const [loading, setLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDropdown, setShowDropdown] = useState(false)
    const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Carregar resultados iniciais ao abrir o dropdown
    const loadInitial = useCallback(async () => {
        if (!isOrgScope && !companyId) return
        setLoading(true)
        try {
            const response = isOrgScope
                ? await searchAccessiblePartners()
                : await searchPartners(companyId!)
            if (response.success) {
                setResults(
                    Array.isArray(response.result) ? response.result : []
                )
            }
        } catch {
            // silencioso
        } finally {
            setLoading(false)
        }
    }, [isOrgScope, companyId])

    // Buscar por termo com debounce
    const searchByTerm = useCallback(
        async (term: string) => {
            if (!isOrgScope && !companyId) return
            setLoading(true)
            try {
                const response = isOrgScope
                    ? await searchAccessiblePartners(term || undefined)
                    : await searchPartners(companyId!, term || undefined)
                if (response.success) {
                    setResults(
                        Array.isArray(response.result) ? response.result : []
                    )
                }
            } catch {
                // silencioso
            } finally {
                setLoading(false)
            }
        },
        [isOrgScope, companyId]
    )

    // Sincroniza o parceiro exibido com o `value` da prop. Reage também quando
    // `value` muda de um id para OUTRO id (ex: "Aplicar parceiro" no modal de
    // comparação seta o partnerId externamente) — não só de null->id.
    useEffect(() => {
        if (!value) {
            setSelectedPartner(null)
            return
        }
        if (selectedPartner?.id === value) return
        if (hasItems) {
            const found = (items || []).find(p => p.id === value)
            if (found) {
                setSelectedPartner(found)
                return
            }
            // Não está nos items pré-carregados (ex: parceiro PARCIAL aplicado
            // pelo modal de comparação — não atende todos os exames). Mesmo
            // assim deve aparecer como selecionado; o operador completa os itens
            // faltantes depois. Cai para a resolução por API abaixo.
        }
        if (!user?.companyId) return
        searchPartners(user.companyId).then(response => {
            if (response.success) {
                const list = Array.isArray(response.result)
                    ? response.result
                    : []
                const found = list.find(p => p.id === value)
                if (found) setSelectedPartner(found)
            }
        })
    }, [value, hasItems, items, selectedPartner])

    // Debounce na digitação
    useEffect(() => {
        if (!showDropdown) return
        if (hasItems) return

        if (debounceRef.current) clearTimeout(debounceRef.current)

        debounceRef.current = setTimeout(() => {
            searchByTerm(searchTerm)
        }, 300)

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [searchTerm, showDropdown, searchByTerm, hasItems])

    // Fechar-ao-clicar-fora mora no hook: o painel vai para um portal no body e deixa
    // de estar dentro do container, então o handler antigo o trataria como clique de
    // fora e fecharia antes de a opção ser escolhida.
    const closeDropdown = useCallback(() => setShowDropdown(false), [])
    const { anchorRef, renderPanel } = useAnchoredDropdown(
        showDropdown && !selectedPartner,
        closeDropdown
    )

    const handleFocus = () => {
        setShowDropdown(true)
        if (hasItems) return
        if (results.length === 0) {
            loadInitial()
        }
    }

    const displayList = hasItems
        ? (() => {
              const term = searchTerm.trim().toLowerCase()
              if (!term) return items as Partner[]
              return (items as Partner[]).filter(
                  p =>
                      p.name?.toLowerCase().includes(term) ||
                      p.tradingName?.toLowerCase().includes(term) ||
                      p.cnpj?.includes(searchTerm) ||
                      p.cpf?.includes(searchTerm)
              )
          })()
        : allowedIds
          ? results.filter(p => allowedIds.includes(p.id))
          : results

    const handleSelect = (partner: Partner) => {
        setSelectedPartner(partner)
        onChange(partner.id, partner)
        setShowDropdown(false)
        setSearchTerm('')
    }

    const handleClear = () => {
        setSelectedPartner(null)
        onChange(null, null)
        setSearchTerm('')
    }

    return (
        <div className={`form-control w-full ${className}`} ref={containerRef}>
            {label && (
                <label className="label">
                    <span className="label-text">{label}</span>
                </label>
            )}

            <div className="relative">
                {selectedPartner ? (
                    <div
                        className={
                            'flex items-center gap-2 input input-bordered w-full pr-10 ' +
                            (size === 'sm' ? 'input-sm!' : '')
                        }
                    >
                        {selectedPartner.personType === 'JURIDICA' ? (
                            <MdBusiness className="text-lg text-secondary flex-shrink-0" />
                        ) : (
                            <MdPerson className="text-lg text-secondary flex-shrink-0" />
                        )}
                        <span className="truncate">
                            {selectedPartner.name ||
                                selectedPartner.tradingName}
                        </span>
                        {!disabled && (
                            <button
                                type="button"
                                onClick={handleClear}
                                className="absolute right-3 top-1/2 -translate-y-1/2 btn btn-ghost btn-xs btn-circle"
                            >
                                <MdClose className="text-lg" />
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="relative" ref={anchorRef}>
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                        <input
                            type="text"
                            placeholder={placeholder}
                            className={
                                'input input-bordered w-full pl-10 ' +
                                (size === 'sm' ? 'input-sm!' : '')
                            }
                            value={searchTerm}
                            onChange={e => {
                                setSearchTerm(e.target.value)
                                setShowDropdown(true)
                            }}
                            onFocus={handleFocus}
                            disabled={disabled}
                            autoComplete="off"
                            aria-autocomplete="none"
                        />
                    </div>
                )}

                {renderPanel(
                    <>
                        {loading ? (
                            <div className="p-4 text-center">
                                <span className="loading loading-spinner loading-sm"></span>
                            </div>
                        ) : displayList.length === 0 ? (
                            <div className="p-4 text-center text-base-content/60 text-sm">
                                {searchTerm
                                    ? 'Nenhum parceiro encontrado'
                                    : 'Nenhum parceiro disponível'}
                            </div>
                        ) : (
                            displayList.map(partner => (
                                <button
                                    key={partner.id}
                                    type="button"
                                    className="w-full px-4 py-3 text-left hover:bg-base-200 flex items-center gap-3 border-b border-base-200 last:border-b-0"
                                    onClick={() => handleSelect(partner)}
                                >
                                    <div className="w-8 h-8 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
                                        {partner.personType === 'JURIDICA' ? (
                                            <MdBusiness className="text-secondary" />
                                        ) : (
                                            <MdPerson className="text-secondary" />
                                        )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="font-medium truncate">
                                            {partner.name}
                                            {priceLabelById?.[partner.id]
                                                ? ` - ${priceLabelById[partner.id]}`
                                                : ''}
                                        </p>
                                        <p className="text-xs text-base-content/60 truncate">
                                            {partner.tradingName && (
                                                <span>
                                                    {partner.tradingName} -{' '}
                                                </span>
                                            )}
                                            {partner.personType === 'JURIDICA'
                                                ? partner.cnpj || 'Sem CNPJ'
                                                : partner.cpf || 'Sem CPF'}
                                        </p>
                                    </div>
                                </button>
                            ))
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
